import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ITEM_FEATURES, RETENTION, formatBytes, weddingsCounted, type PremiumFeature, type WeddingUsageDTO } from "@bridewell/shared";
import { Briefcase, ChevronRight, Sparkles } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { authClient } from "@/api/auth";
import { ApiError, authApiError, exportUrl, meQuery, usageQuery, useUpdateMe } from "@bridewell/api-client";
import { NotificationsCard } from "@/features/account/notifications-card";
import { SecurityCard } from "@/features/account/security-card";
import { SignInMethodsCard, useLinkedAccounts } from "@/features/account/sign-in-methods-card";
import { refreshSession } from "@/api/session";
import { PageHeader } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, SectionTitle } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/field";
import { Chip, EmptyState, Monogram, Spinner } from "@/components/ui/misc";
import { ThemeSwitcher } from "@/components/ui/theme-switcher";
import { FormSheet } from "@/components/ui/sheet";
import { useToast } from "@/components/ui/toast";
import { UsageMeter, meterTone } from "@/components/ui/usage-meter";
import { t, type TKey } from "@/i18n";
import { LEGAL } from "@/legal/config";
import { useConsent } from "@/lib/consent";
import { errorMessage } from "@/lib/error-reporting";
import { cn, coupleTitle } from "@/lib/utils";

// `linked` / `error`: back from adding Apple or Google as a way to sign in (sign-in-methods-card.tsx).
const search = z.object({ linked: z.enum(["apple", "google"]).optional().catch(undefined), error: z.string().max(80).optional().catch(undefined) });

export const Route = createFileRoute("/app/account")({ validateSearch: search, component: AccountPage });

function AccountPage() {
  const me = useQuery(meQuery);
  const usage = useQuery(usageQuery);
  const { linked, error: linkError } = Route.useSearch();
  const accounts = useLinkedAccounts();
  // An account that signs in only with Apple or Google has no password to confirm deletion with: a recent sign-in is the proof.
  const hasPassword = accounts.data?.some((a) => a.providerId === "credential") ?? true;
  const [staleSession, setStaleSession] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { openSettings } = useConsent();
  const [name, setName] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");

  const updateMe = useUpdateMe();
  const rename = {
    isPending: updateMe.isPending,
    mutate: (n: string) => updateMe.mutate({ name: n }, {
      onSuccess: async () => { await refreshSession(qc); toast("Saved", "success"); },
      onError: (e) => toast(errorMessage(e), "danger"),
    }),
  };
  // Deleting an account is the one irreversible action: it takes the current password, verified by the server
  // (a stolen or idle session is not enough). Without a password, Better Auth takes a session from the last 24 hours.
  const del = useMutation({
    mutationFn: async (password: string) => { const r = await authClient.deleteUser(hasPassword ? { password } : {}); if (r.error) throw authApiError(r.error); },
    onSuccess: async () => { qc.clear(); void navigate({ to: "/login" }); },
    onError: (e) => {
      if (!hasPassword && e instanceof ApiError && e.code === "SESSION_EXPIRED") { setStaleSession(true); return; }
      toast(errorMessage(e), "danger");
    },
  });

  if (me.isPending) return <Spinner />;
  if (me.isError) return <><PageHeader title={t("common.account")} /><EmptyState title={t("common.somethingWrong")} body={errorMessage(me.error)} action={<Button variant="secondary" onClick={() => void me.refetch()}>{t("common.retry")}</Button>} /></>;
  if (!me.data) return null;
  const u = me.data;
  const paid = u.plan !== "free";
  const planLabel = t(`plan.${u.plan}` as TKey);

  return (
    <>
      <PageHeader title={t("common.account")} subtitle={t("auth.platforms")} />

      <SectionTitle>Profile</SectionTitle>
      <Card className="flex flex-col gap-4">
        <div className="grid gap-4 md:grid-cols-2">
          {/* The button sits next to the field, not inside its <label>: a label's text is the input's accessible name. */}
          <div className="flex items-end gap-2">
            <Field label={t("common.name")} className="flex-1">
              <Input value={name ?? u.name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Button variant="secondary" disabled={name === null || name.trim() === "" || name === u.name} loading={rename.isPending} onClick={() => rename.mutate(name!.trim())}>{t("common.save")}</Button>
          </div>
          <Field label={t("common.email")}><Input value={u.email} disabled /></Field>
        </div>
        <Link to="/app/billing" className="flex items-center justify-between rounded-control bg-bg px-3 py-2 text-sm hover:bg-accent/8">
          <span className="text-text-secondary">Plan</span>
          <span className="flex items-center gap-1.5 font-semibold">
            {paid && (u.plan === "enterprise" ? <Briefcase className="h-3.5 w-3.5 text-accent" /> : <Sparkles className="h-3.5 w-3.5 text-accent" />)}
            {planLabel}
            <ChevronRight className="h-4 w-4 text-text-tertiary" />
          </span>
        </Link>
      </Card>

      <SectionTitle className="mt-6" action={<Link to="/app/billing" className="text-sm font-semibold text-accent">{t("usage.manage")}</Link>}>{t("usage.title")}</SectionTitle>
      <Card className="flex flex-col gap-4">
        {usage.data ? (
          <>
            {/* The free tier counts archived weddings against its one slot; paid tiers only count active ones. */}
            <UsageMeter
              label={usage.data.weddings.countsArchived ? t("usage.weddingsAll") : t("usage.weddings")}
              used={weddingsCounted(usage.data.weddings)}
              limit={usage.data.weddings.unlimited ? null : usage.data.weddings.limit}
              hint={usage.data.weddings.archived ? t(usage.data.weddings.countsArchived ? "usage.archivedCounted" : "usage.archivedFree", { n: usage.data.weddings.archived }) : undefined}
            />
            <UsageMeter label={t("usage.storage")} used={usage.data.storage.usedBytes} limit={usage.data.storage.limitBytes} format={formatBytes} />
            {usage.data.templates.limit > 0 && <UsageMeter label={t("usage.templates")} used={usage.data.templates.count} limit={usage.data.templates.limit} />}
          </>
        ) : usage.isError ? <EmptyState title={t("common.somethingWrong")} body={errorMessage(usage.error)} action={<Button variant="secondary" onClick={() => void usage.refetch()}>{t("common.retry")}</Button>} /> : <Spinner />}
      </Card>

      {usage.data && (
        <>
          <SectionTitle className="mt-6" action={usage.data.itemLimits ? <Link to="/app/billing" className="text-sm font-semibold text-accent">{t("usage.upgrade")}</Link> : undefined}>{t("usage.perWedding")}</SectionTitle>
          <p className="mb-2 px-1 text-xs text-text-tertiary">{usage.data.itemLimits ? t("usage.perWeddingFree") : t("usage.perWeddingPaid", { plan: planLabel })}</p>
          {usage.data.ownedWeddings.length === 0 ? (
            <Card className="text-sm text-text-secondary">{t("usage.noOwned")}</Card>
          ) : (
            <div className="flex flex-col gap-3">
              {usage.data.ownedWeddings.map((w) => <WeddingUsage key={w.id} w={w} limits={usage.data!.itemLimits} />)}
            </div>
          )}
        </>
      )}

      <NotificationsCard />

      <SectionTitle className="mt-6">{t("common.appearance")}</SectionTitle>
      <Card>
        <ThemeSwitcher segmented />
      </Card>

      <SignInMethodsCard me={u} linked={linked} linkError={linkError} />

      <SecurityCard me={u} hasPassword={hasPassword} />

      <SectionTitle className="mt-6">{t("legal.section")}</SectionTitle>
      <Card className="flex flex-col divide-y divide-border py-1">
        {([
          { to: "/legal/privacy", label: t("legal.privacy") },
          { to: "/legal/terms", label: t("legal.terms") },
          { to: "/legal/terms", hash: "refunds", label: t("legal.refunds") },
          { to: "/legal/cookies", label: t("legal.cookies") },
        ] as const).map((l) => (
          <Link key={l.label} to={l.to} hash={"hash" in l ? l.hash : undefined} className="flex items-center justify-between py-2.5 text-[15px] hover:text-accent">{l.label}<ChevronRight className="h-4 w-4 text-text-tertiary" /></Link>
        ))}
        <button type="button" onClick={openSettings} className="flex items-center justify-between py-2.5 text-left text-[15px] hover:text-accent">{t("legal.cookieSettings")}<ChevronRight className="h-4 w-4 text-text-tertiary" /></button>
        <a href={exportUrl.account} download className="flex items-center justify-between py-2.5 text-[15px] hover:text-accent">
          <span>{t("exports.account")}<span className="block text-xs text-text-tertiary">{t("exports.accountHint")}</span></span><ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" />
        </a>
        <a href={`mailto:${LEGAL.operator.privacyEmail}?subject=${encodeURIComponent(`Data request (${u.email})`)}`} className="flex items-center justify-between py-2.5 text-[15px] hover:text-accent">{t("legal.requestData")}<ChevronRight className="h-4 w-4 text-text-tertiary" /></a>
        <Link to="/contact" className="flex items-center justify-between py-2.5 text-[15px] hover:text-accent">{t("account.contact")}<ChevronRight className="h-4 w-4 text-text-tertiary" /></Link>
      </Card>

      <SectionTitle className="mt-6">Danger zone</SectionTitle>
      <Card className="flex flex-col gap-2">
        <p className="text-sm text-text-secondary">Deleting your account removes the weddings you own and all their data, and leaves any wedding you've joined. Everything is permanently erased within {RETENTION.softDeleteDays} days.</p>
        <Button variant="danger" onClick={() => setConfirmDelete(true)}>Delete account</Button>
      </Card>

      <FormSheet open={confirmDelete} onClose={() => { setConfirmDelete(false); setDeletePassword(""); }} onSubmit={() => del.mutate(deletePassword)} busy={del.isPending} size="sm"
        title={t("account.deleteTitle")} submitLabel={t("account.deleteConfirm")}>
        <p className="text-[15px] text-text-secondary">{t("account.deleteBody")}{paid ? ` ${t("account.deleteBodyPaid")}` : ""}</p>
        {hasPassword ? (
          <Field label={t("account.deletePassword")} hint={t("account.deletePasswordHint")}>
            <Input type="password" autoComplete="current-password" required autoFocus value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} />
          </Field>
        ) : (
          <>
            <p className="text-sm text-text-tertiary">{t("account.deleteFresh")}</p>
            {staleSession && (
              <Button variant="secondary" onClick={async () => { await authClient.signOut(); qc.clear(); void navigate({ to: "/login", search: { redirect: "/app/account" } }); }}>
                {t("account.deleteSignInAgain")}
              </Button>
            )}
          </>
        )}
      </FormSheet>
    </>
  );
}

/** One owned wedding: every capped section's rows against the plan's cap (or "Unlimited"), plus collaborators. */
function WeddingUsage({ w, limits }: { w: WeddingUsageDTO; limits: Readonly<Record<PremiumFeature, number>> | null }) {
  const rows = [
    ...ITEM_FEATURES.map((f) => ({ key: f as string, label: t(`modules.${f}` as TKey), used: w.items[f], limit: limits ? limits[f] : null })),
    { key: "collaborators", label: t("usage.collaborators"), used: w.collaborators, limit: limits ? limits.collaborators : null },
  ];
  const full = rows.filter((r) => meterTone(r.used, r.limit) === "full").length;
  const near = rows.filter((r) => meterTone(r.used, r.limit) === "warn").length;
  const summary = w.archived ? t("lifecycle.archived") : !limits ? t("usage.unlimitedItems") : full ? t("usage.atLimit", { n: full }) : near ? t("usage.nearLimit", { n: near }) : t("usage.roomEverywhere");
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Monogram one={w.partnerOneName} two={w.partnerTwoName} size={36} className={cn(w.archived && "opacity-60 grayscale")} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">{coupleTitle(w.partnerOneName, w.partnerTwoName)}</div>
          <div className="text-xs text-text-tertiary">{summary}</div>
        </div>
        {full > 0 && <Chip small tint="var(--danger)">{t("usage.atLimit", { n: full })}</Chip>}
      </div>
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {rows.map((r) => <UsageMeter key={r.key} compact label={r.label} used={r.used} limit={r.limit} />)}
      </div>
    </Card>
  );
}
